import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import { BsExclamationTriangleFill, BsX, BsClockHistory, BsShieldExclamation } from "react-icons/bs";
import axios from "axios";

export default function PackageExpiryBanner({ user }) {
  const [validityInfo, setValidityInfo] = useState(user?.package_validity || null);
  const [dismissed, setDismissed] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (user?.package_validity) {
      setValidityInfo(user.package_validity);
    }
  }, [user?.package_validity]);

  useEffect(() => {
    // Only check if user is logged in and not superadmin
    if (!user?.token || user?.is_superuser || user?.main_role === "SuperAdmin") {
      return;
    }

    const checkValidity = async () => {
      try {
        const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await axios.get(
          `${process.env.REACT_APP_URL}package/validity-status/?timezone=${encodeURIComponent(clientTz)}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        if (res?.data?.data) {
          setValidityInfo(res.data.data);
        }
      } catch (err) {
        // Silently catch network errors
      }
    };

    checkValidity();
  }, [user?.token, user?.id]);

  // Handle popup display (one time per session per validity date)
  useEffect(() => {
    if (validityInfo?.is_expiring_soon && validityInfo?.validity_date) {
      const popupKey = `dismiss_package_popup_${validityInfo.validity_date}`;
      if (sessionStorage.getItem(popupKey) !== "true") {
        setPopupOpen(true);
      }
    }
  }, [validityInfo]);

  const handleClosePopup = () => {
    if (validityInfo?.validity_date) {
      sessionStorage.setItem(`dismiss_package_popup_${validityInfo.validity_date}`, "true");
    }
    setPopupOpen(false);
  };

  const daysLeft = validityInfo?.days_left ?? 0;
  const isUrgent = daysLeft <= 2;

  let messageText = "";
  if (daysLeft === 0) {
    messageText = "Your package subscription expires today! Please contact Admin immediately to renew and prevent service disruption.";
  } else if (daysLeft === 1) {
    messageText = `Your package subscription expires tomorrow (${validityInfo?.validity_date}). Please contact Admin to renew.`;
  } else {
    messageText = `Your package subscription will expire in ${daysLeft} days (on ${validityInfo?.validity_date}). Please contact Admin to renew.`;
  }

  const sessionKey = validityInfo?.validity_date ? `dismiss_package_expiry_${validityInfo.validity_date}` : null;
  const isBannerDismissed = dismissed || (sessionKey && sessionStorage.getItem(sessionKey) === "true");

  const handleDismissBanner = () => {
    if (sessionKey) {
      sessionStorage.setItem(sessionKey, "true");
    }
    setDismissed(true);
  };

  if (!validityInfo || !validityInfo.is_expiring_soon) {
    return null;
  }

  return (
    <>
      {/* 1. Modal Popup Notice */}
      <Dialog
        open={popupOpen}
        onClose={handleClosePopup}
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            p: 1.5,
            maxWidth: 480,
            width: "100%",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
            border: isUrgent ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid rgba(245, 158, 11, 0.4)",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: isUrgent ? "#fee2e2" : "#fef3c7",
                color: isUrgent ? "#dc2626" : "#d97706",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                flexShrink: 0
              }}
            >
              <BsExclamationTriangleFill />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, color: "#0f172a" }}>
                {isUrgent ? "Package Expiring Soon!" : "Subscription Notice"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Notice for upcoming package expiration
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClosePopup} size="small" sx={{ color: "#94a3b8" }}>
            <BsX size={26} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: "#1e293b", mb: 2, lineHeight: 1.6, fontSize: "15px" }}>
            {validityInfo.company_name ? (
              <strong>{validityInfo.company_name}: </strong>
            ) : null}
            Your package subscription {validityInfo.package_title ? `("${validityInfo.package_title}")` : ""} will expire in{" "}
            <span style={{ color: isUrgent ? "#dc2626" : "#d97706", fontWeight: 700, fontSize: "16px" }}>
              {daysLeft === 0 ? "today" : `${daysLeft} days`}
            </span>{" "}
            (on <strong>{validityInfo.validity_date}</strong>).
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isUrgent ? "rgba(239, 68, 68, 0.08)" : "rgba(245, 158, 11, 0.08)",
              border: isUrgent ? "1px solid rgba(239, 68, 68, 0.25)" : "1px solid rgba(245, 158, 11, 0.25)",
            }}
          >
            <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.5 }}>
              Please contact your system administrator to renew the package to prevent any service interruption.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            variant="contained"
            className="btn-primary"
            onClick={handleClosePopup}
            sx={{
              borderRadius: 2,
              px: 3.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px"
            }}
          >
            I Understand / Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Top Warning Banner */}
      {!isBannerDismissed && (
      <Box
        sx={{
          margin: "8px 16px 12px 16px",
          padding: "10px 16px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          background: isUrgent
            ? "linear-gradient(90deg, #7f1d1d 0%, #991b1b 100%)"
            : "linear-gradient(90deg, #78350f 0%, #92400e 100%)",
          color: "#ffffff",
          boxShadow: isUrgent
            ? "0 4px 14px rgba(220, 38, 38, 0.35)"
            : "0 4px 14px rgba(217, 119, 6, 0.3)",
          border: isUrgent ? "1px solid #ef4444" : "1px solid #f59e0b",
          zIndex: 10,
          animation: "fadeIn 0.3s ease-in-out"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: isUrgent ? "rgba(239, 68, 68, 0.3)" : "rgba(245, 158, 11, 0.3)",
              flexShrink: 0
            }}
          >
            {isUrgent ? (
              <BsExclamationTriangleFill size={18} color="#fca5a5" />
            ) : (
              <BsClockHistory size={18} color="#fde68a" />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "14px", letterSpacing: "0.2px" }}>
              {isUrgent ? "Subscription Expiring Soon!" : "Subscription Renewal Notice"}
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.95, lineHeight: 1.3 }}>
              {validityInfo.company_name ? (
                <strong>{validityInfo.company_name}: </strong>
              ) : null}
              {messageText}
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleDismissBanner}
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            "&:hover": { color: "#ffffff", backgroundColor: "rgba(255, 255, 255, 0.15)" },
            flexShrink: 0
          }}
          title="Dismiss notification"
        >
          <BsX size={24} />
        </IconButton>
      </Box>
      )}
    </>
  );
}
